<template>
    <div class="ticket-aside space-y-5">
        <!-- Edit ticket -->
        <div v-if="ticket.can_edit && ticket.uuid" class="ticket-aside-edit">
            <router-link
                :to="'/dashboard/tickets/' + ticket.uuid + '/edit'"
                class="btn btn-blue shadow-sm rounded-md w-full text-center inline-flex items-center justify-center gap-2"
            >
                <svg-vue class="h-4 w-4" icon="font-awesome.pencil-alt-regular"></svg-vue>
                {{ $t('Edit ticket') }}
            </router-link>
        </div>

        <!-- Follow-up: kirim ulang notifikasi chat terbaru ke semua peserta.
             CC tidak boleh follow-up (bukan peserta yang bertindak). -->
        <div v-if="ticket.uuid && !isCcParticipant" class="ticket-aside-follow-up">
            <button
                class="btn btn-white border border-gray-300 shadow-sm rounded-md w-full inline-flex items-center justify-center gap-2"
                :class="followUpDisabled ? 'opacity-60 cursor-not-allowed' : ''"
                :disabled="followUpDisabled"
                :title="$t('Resend the latest chat notification to all participants?')"
                type="button"
                @click="$emit('follow-up')"
            >
                <svg-vue class="h-4 w-4" :class="followUpLoading ? 'animate-spin' : ''" :icon="followUpLoading ? 'font-awesome.sync-regular' : 'font-awesome.bell-regular'"></svg-vue>
                {{ followUpLoading ? $t('Sending') : $t('Follow-up') }}
            </button>
            <div v-if="followUpCooldownLabel" class="mt-1 text-xs text-gray-500 text-center">
                {{ followUpCooldownLabel }}
            </div>
        </div>

        <!-- Creator -->
        <div v-if="ticket.user" class="ticket-aside-customer">
            <div class="ticket-aside-label">{{ $t('Creator') }}</div>
            <div class="flex items-center gap-3">
                <img
                    :alt="$t('Avatar')"
                    :src="customerAvatar"
                    class="h-12 w-12 rounded-full flex-shrink-0 object-cover cursor-zoom-in"
                    @click="openAvatarViewer(ticket.user)"
                />
                <div class="min-w-0">
                    <div class="text-gray-900 font-semibold truncate">{{ ticket.user.name }}</div>
                    <div class="flex items-center text-sm text-gray-600 min-w-0">
                        <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4" icon="font-awesome.envelope-solid"></svg-vue>
                        <span class="truncate">{{ ticket.user.email }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- End date range -->
        <div v-if="ticket.started_at || ticket.due_at" class="ticket-aside-section">
            <div class="ticket-aside-label">{{ $t('End date') }}</div>
            <div
                class="mt-1 flex items-center gap-2 text-sm"
                :class="deadlineUrgent ? 'text-red-700 font-semibold' : 'text-gray-800'"
            >
                <svg-vue class="h-4 w-4 flex-shrink-0" icon="font-awesome.calendar-regular"></svg-vue>
                <span>
                    <template v-if="ticket.started_at">{{ formatDate(ticket.started_at) }}</template>
                    <template v-if="ticket.started_at && ticket.due_at"> → </template>
                    <template v-if="ticket.due_at">{{ formatDate(ticket.due_at) }}</template>
                </span>
                <span
                    v-if="deadlineUrgent"
                    class="inline-flex items-center px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold"
                >
                    {{ $t('Due soon') }}
                </span>
            </div>
        </div>

        <!-- Executors -->
        <div class="ticket-aside-section">
            <div class="ticket-aside-label">{{ $t('Executors') }} ({{ executors.length }})</div>
            <div v-if="executors.length === 0" class="mt-1 text-sm text-gray-400">{{ $t('Unassigned') }}</div>
            <ul v-else class="mt-2 space-y-2">
                <li
                    v-for="user in executors"
                    :key="'aside-executor-' + user.id"
                    class="flex items-center gap-2"
                >
                    <img
                        v-if="avatar(user)"
                        :src="avatar(user)"
                        class="h-8 w-8 rounded-full flex-shrink-0 object-cover cursor-zoom-in"
                        alt="avatar"
                        @click="openAvatarViewer(user)"
                    />
                    <div class="min-w-0">
                        <div class="text-sm text-gray-900 font-medium truncate">{{ user.name }}</div>
                        <div class="flex items-center text-xs text-gray-600 min-w-0">
                            <svg-vue class="flex-shrink-0 mr-1 h-3.5 w-3.5" icon="font-awesome.envelope-solid"></svg-vue>
                            <span class="truncate">{{ user.email }}</span>
                        </div>
                    </div>
                </li>
            </ul>
        </div>

        <!-- CC / Diketahui -->
        <div class="ticket-aside-section">
            <div class="flex items-center justify-between gap-2">
                <div class="ticket-aside-label">{{ $t('CC / Diketahui') }} ({{ ccs.length }})</div>
                <button
                    v-if="canManageCc"
                    class="text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
                    type="button"
                    @click="toggleCcEditor"
                >
                    {{ ccEditorOpen ? $t('Cancel') : $t('Edit') }}
                </button>
            </div>
            <div v-if="ccs.length === 0 && !ccEditorOpen" class="mt-1 text-sm text-gray-400">{{ $t('No CC selected') }}</div>
            <ul v-if="ccs.length > 0" class="mt-2 space-y-2">
                <li
                    v-for="user in ccs"
                    :key="'aside-cc-' + user.id"
                    class="flex items-center gap-2"
                >
                    <img
                        v-if="avatar(user)"
                        :src="avatar(user)"
                        class="h-8 w-8 rounded-full flex-shrink-0 object-cover cursor-zoom-in"
                        alt="avatar"
                        @click="openAvatarViewer(user)"
                    />
                    <div class="min-w-0">
                        <div class="text-sm text-gray-900 font-medium truncate">{{ user.name }}</div>
                        <div class="flex items-center text-xs text-gray-600 min-w-0">
                            <svg-vue class="flex-shrink-0 mr-1 h-3.5 w-3.5" icon="font-awesome.envelope-solid"></svg-vue>
                            <span class="truncate">{{ user.email }}</span>
                        </div>
                    </div>
                </li>
            </ul>

            <!-- Editor CC: hanya pembuat, admin, dan eksekutor. Opsi reaktif
                 menyembunyikan eksekutor, approver, dan CC yang sudah dipilih. -->
            <div v-if="ccEditorOpen" class="mt-3 rounded-lg border border-purple-200 bg-purple-50/50 p-2 space-y-2">
                <input
                    v-model.trim="ccUserSearch"
                    :placeholder="$t('Search user')"
                    class="form-input block w-full text-sm"
                    type="text"
                >
                <div class="max-h-40 overflow-y-auto rounded border border-gray-200 bg-white">
                    <button
                        v-for="user in ccOptions"
                        :key="'aside-cc-option-' + user.id"
                        class="w-full flex items-center px-2 py-1.5 text-left hover:bg-purple-50 focus:outline-none"
                        type="button"
                        @click="addCcDraftUser(user.id)"
                    >
                        <img :src="avatar(user)" class="h-6 w-6 rounded-full mr-2 object-cover" alt="avatar">
                        <span class="min-w-0 flex-1">
                            <span class="block text-sm text-gray-700 truncate">{{ user.name }}</span>
                            <span class="block text-xs text-gray-400 truncate">{{ user.email }}</span>
                        </span>
                    </button>
                    <div v-if="ccOptions.length === 0" class="px-2 py-1.5 text-sm text-gray-500">
                        {{ $t('No users found') }}
                    </div>
                </div>
                <div v-if="ccDraftUsers.length > 0" class="flex flex-wrap gap-1">
                    <span
                        v-for="user in ccDraftUsers"
                        :key="'aside-cc-draft-chip-' + user.id"
                        class="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs border border-purple-200"
                    >
                        {{ user.name }}
                        <button class="ml-1 text-purple-600 hover:text-red-600" type="button" @click="removeCcDraftUser(user.id)">×</button>
                    </span>
                </div>
                <div v-else class="text-xs text-gray-400">{{ $t('No CC selected') }}</div>
                <div class="flex justify-end pt-1">
                    <button
                        class="btn btn-blue shadow-sm rounded-md text-sm"
                        :class="ccSaving ? 'opacity-60 cursor-not-allowed' : ''"
                        :disabled="ccSaving"
                        type="button"
                        @click="saveCc"
                    >
                        {{ ccSaving ? $t('Saving') : $t('Save') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Approvers -->
        <div v-if="ticket.approval_required" class="ticket-aside-section">
            <div class="ticket-aside-label">{{ $t('Approvers') }} ({{ approvers.length }})</div>
            <div v-if="approvers.length === 0" class="mt-1 text-sm text-gray-400">{{ $t('Unassigned') }}</div>
            <div v-else class="mt-2 space-y-2">
                <div
                    v-for="approver in approvers"
                    :key="'aside-approver-' + approver.id"
                    class="flex items-center justify-between gap-2 p-1.5 rounded-md"
                    :class="approver.is_turn ? 'bg-amber-50/60 border border-amber-200' : ''"
                >
                    <div class="flex items-center min-w-0">
                        <span
                            v-if="hasMultipleTiers"
                            class="mr-2 inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold"
                            :class="approver.order === 1 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'"
                            :title="approver.order === 1 ? $t('Creator department approver') : $t('Other department approver')"
                        >
                            {{ approver.order }}
                        </span>
                        <img
                            v-if="avatar(approver.user)"
                            :src="avatar(approver.user)"
                            class="h-8 w-8 rounded-full mr-2 flex-shrink-0 object-cover cursor-zoom-in"
                            alt="avatar"
                            @click="openAvatarViewer(approver.user)"
                        >
                        <div class="min-w-0">
                            <div class="text-sm text-gray-800 truncate">{{ approver.user ? approver.user.name : '-' }}</div>
                            <div class="flex items-center text-xs text-gray-600 min-w-0">
                                <svg-vue class="flex-shrink-0 mr-1 h-3.5 w-3.5" icon="font-awesome.envelope-solid"></svg-vue>
                                <span class="truncate">{{ approver.user ? approver.user.email : '-' }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-col items-end gap-0.5 flex-shrink-0">
                        <span :class="approverStatusClass(approver.status)" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold">
                            {{ approverStatusLabel(approver.status) }}
                        </span>
                        <span v-if="ticket.approval_status === 'pending' && approver.status === 'pending' && !approver.is_turn" class="text-[10px] text-gray-400 font-medium">
                            {{ $t('Waiting your turn') }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Approval status card -->
        <div
            v-if="ticket.approval_required"
            :class="approvalFlowClass"
            class="rounded-lg border p-3 text-sm"
        >
            <div class="flex items-center justify-between">
                <div class="font-semibold">{{ $t('Approval') }}</div>
                <span :class="approvalStatusBadgeClass" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold">
                    {{ approvalStatusLabel }}
                </span>
            </div>

            <div v-if="ticket.approval_status === 'pending'" class="mt-2">
                <div class="text-xs text-gray-600">{{ $t('Waiting approval') }}: {{ approverNames }}</div>
                <div v-if="isWaitingOtherTier" class="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">
                    {{ $t('Waiting for approval from the ticket creator department first') }}
                </div>
                <div class="mt-2 flex flex-wrap gap-2">
                    <button
                        v-if="ticket.can_approve"
                        class="btn btn-green shadow-sm rounded-md"
                        type="button"
                        @click="$emit('approve')"
                    >{{ $t('Approve ticket') }}</button>
                    <button
                        v-if="ticket.can_reject"
                        class="btn btn-red shadow-sm rounded-md"
                        type="button"
                        @click="$emit('reject')"
                    >{{ $t('Reject ticket') }}</button>
                </div>
            </div>

            <div v-else class="mt-2 text-xs text-gray-600">
                <template v-if="ticket.approval_status === 'approved'">
                    {{ $t('Approved by') }}: <span class="font-medium">{{ ticket.approved_by_user ? ticket.approved_by_user.name : '-' }}</span>
                </template>
                <template v-else-if="ticket.approval_status === 'rejected'">
                    {{ $t('Rejected by') }}: <span class="font-medium">{{ ticket.rejected_by_user ? ticket.rejected_by_user.name : '-' }}</span>
                </template>
                <template v-else-if="ticket.approval_status === 'canceled'">
                    {{ $t('Canceled by') }}: <span class="font-medium">{{ ticket.canceled_by_user ? ticket.canceled_by_user.name : '-' }}</span>
                </template>
            </div>
        </div>

        <!-- Avatar viewer popup -->
        <image-lightbox
            :open="avatarViewerOpen"
            :url="avatarViewerUrl"
            :name="avatarViewerName"
            :email="avatarViewerEmail"
            :alt="avatarViewerName"
            @close="closeAvatarViewer"
        />
    </div>
</template>

<script>
export default {
    name: 'TicketAside',
    props: {
        ticket: {
            type: Object,
            required: true,
        },
        // Status tombol follow-up dikendalikan halaman induk, karena
        // request & cooldown-nya dimiliki di sana.
        followUpLoading: {
            type: Boolean,
            default: false,
        },
        followUpRemainingMinutes: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            avatarViewerOpen: false,
            avatarViewerUrl: '',
            avatarViewerName: '',
            avatarViewerEmail: '',
            ccEditorOpen: false,
            ccUserSearch: '',
            ccUserList: [],
            ccUserListLoaded: false,
            ccDraftIds: [],
            ccSaving: false,
        };
    },
    computed: {
        executors() {
            return this.ticket.targets || [];
        },
        ccs() {
            return this.ticket.ccs || [];
        },
        isCcParticipant() {
            return (this.ticket.cc_user_ids || []).indexOf(this.currentUserId) > -1;
        },
        currentUserId() {
            const user = this.$store && this.$store.state.user ? this.$store.state.user : null;
            return user ? Number(user.id) : null;
        },
        isAdminUser() {
            const user = this.$store && this.$store.state.user ? this.$store.state.user : null;
            return !!(user && Number(user.role_id) === 1);
        },
        canManageCc() {
            // Hanya pembuat, admin, dan eksekutor yang boleh mengatur CC.
            // Approver dan sesama CC tidak boleh.
            if (this.isAdminUser || this.ticket.is_creator) {
                return true;
            }
            if (Number(this.ticket.agent_id) === this.currentUserId) {
                return true;
            }
            return (this.ticket.target_user_ids || []).indexOf(this.currentUserId) > -1;
        },
        // Opsi CC menyembunyikan secara reaktif: diri sendiri, eksekutor,
        // approver, dan CC yang sudah dipilih — satu orang satu peran.
        ccOptions() {
            const hiddenIds = this.mergeIds(
                [this.currentUserId],
                this.ticket.target_user_ids || [],
                this.ticket.approval_user_ids || [],
                this.ccDraftIds
            );
            const text = String(this.ccUserSearch || '').toLowerCase().trim();
            return this.ccUserList
                .filter((user) => hiddenIds.indexOf(Number(user.id)) === -1)
                .filter((user) => {
                    if (!text) {
                        return true;
                    }
                    return String(user.name || '').toLowerCase().indexOf(text) > -1
                        || String(user.email || '').toLowerCase().indexOf(text) > -1;
                })
                .slice(0, 30);
        },
        ccDraftUsers() {
            const known = {};
            this.ccUserList.forEach((user) => {
                known[Number(user.id)] = user;
            });
            this.ccs.forEach((user) => {
                if (!known[Number(user.id)]) {
                    known[Number(user.id)] = user;
                }
            });
            return this.ccDraftIds
                .map((id) => known[Number(id)])
                .filter(Boolean);
        },
        followUpDisabled() {
            return this.followUpLoading || this.followUpRemainingMinutes > 0;
        },
        followUpCooldownLabel() {
            if (this.followUpRemainingMinutes < 1) {
                return '';
            }
            return this.$t('Please wait {minutes} more minutes before following up again', {
                minutes: this.followUpRemainingMinutes,
            }).toString();
        },
        approvers() {
            return this.ticket.approvers || [];
        },
        approverNames() {
            if (!this.approvers || this.approvers.length === 0) {
                return '-';
            }
            return this.approvers
                .map((item) => (item.user ? item.user.name : '-'))
                .join(', ');
        },
        deadlineUrgent() {
            if (!this.ticket.due_at) {
                return false;
            }
            const due = moment(this.ticket.due_at).startOf('day');
            const limit = moment().startOf('day').add(3, 'days');
            return due.isSameOrBefore(limit);
        },
        approvalFlowClass() {
            if (this.ticket.approval_status === 'approved') {
                return 'border-green-300 bg-green-50 text-green-900';
            }
            if (this.ticket.approval_status === 'rejected' || this.ticket.approval_status === 'canceled') {
                return 'border-red-300 bg-red-50 text-red-900';
            }
            return 'border-yellow-300 bg-yellow-50 text-yellow-900';
        },
        approvalStatusBadgeClass() {
            if (this.ticket.approval_status === 'approved') {
                return 'bg-green-100 text-green-800';
            }
            if (this.ticket.approval_status === 'rejected' || this.ticket.approval_status === 'canceled') {
                return 'bg-red-100 text-red-800';
            }
            return 'bg-yellow-100 text-yellow-800';
        },
        approvalStatusLabel() {
            if (this.ticket.approval_status === 'approved') {
                return this.$t('Approved');
            }
            if (this.ticket.approval_status === 'rejected') {
                return this.$t('Rejected');
            }
            if (this.ticket.approval_status === 'canceled') {
                return this.$t('Canceled');
            }
            return this.$t('Pending');
        },
        customerAvatar() {
            const user = this.ticket.user || {};
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
        hasMultipleTiers() {
            const orders = new Set((this.approvers || []).map((a) => a.order).filter(Boolean));
            return orders.size > 1;
        },
        isWaitingOtherTier() {
            if (this.ticket.approval_status !== 'pending' || this.ticket.can_approve) {
                return false;
            }
            // User is in approvers list with pending status, but not allowed to approve yet (is_turn is false)
            const currentUserId = window.app && window.app.user ? window.app.user.id : null;
            if (!currentUserId) {
                return false;
            }
            const myApprover = (this.approvers || []).find((a) => a.user && a.user.id === currentUserId);
            return myApprover && myApprover.status === 'pending' && !myApprover.is_turn;
        },
    },
    methods: {
        openAvatarViewer(user) {
            if (!user) {
                return;
            }
            const url = this.avatar(user);
            if (!url) {
                return;
            }
            this.avatarViewerUrl = url;
            this.avatarViewerName = user.name || '';
            this.avatarViewerEmail = user.email || '';
            this.avatarViewerOpen = true;
        },
        closeAvatarViewer() {
            this.avatarViewerOpen = false;
            this.avatarViewerUrl = '';
            this.avatarViewerName = '';
            this.avatarViewerEmail = '';
        },
        mergeIds() {
            const seen = {};
            const result = [];
            Array.prototype.slice.call(arguments).forEach((values) => {
                (values || []).forEach((value) => {
                    const id = Number(value);
                    if (!id || seen[id]) {
                        return;
                    }
                    seen[id] = true;
                    result.push(id);
                });
            });
            return result;
        },
        toggleCcEditor() {
            if (this.ccEditorOpen) {
                this.ccEditorOpen = false;
                return;
            }
            this.ccDraftIds = (this.ticket.cc_user_ids || []).map((id) => Number(id));
            this.ccUserSearch = '';
            this.ccEditorOpen = true;
            if (!this.ccUserListLoaded) {
                this.fetchCcUserList();
            }
        },
        fetchCcUserList() {
            const self = this;
            axios.get('api/dashboard/tickets/filters', {
                params: {
                    context: 'assignment',
                    _t: Date.now(),
                }
            }).then((response) => {
                self.ccUserList = (response.data.targets || response.data.agents || [])
                    .filter((user) => Number(user.id) !== self.currentUserId && Number(user.role_id) !== 1);
                self.ccUserListLoaded = true;
            });
        },
        addCcDraftUser(userId) {
            const id = Number(userId);
            if (!id || this.ccDraftIds.indexOf(id) > -1) {
                this.ccUserSearch = '';
                return;
            }
            this.ccDraftIds.push(id);
            this.ccUserSearch = '';
        },
        removeCcDraftUser(userId) {
            const id = Number(userId);
            this.ccDraftIds = this.ccDraftIds.filter((value) => value !== id);
        },
        saveCc() {
            if (this.ccSaving) {
                return;
            }
            this.ccSaving = true;
            const self = this;
            axios.post('api/dashboard/tickets/' + this.ticket.uuid + '/quick-actions', {
                action: 'cc',
                cc_user_ids: this.ccDraftIds,
            }).then(() => {
                self.ccEditorOpen = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('CC list has been updated').toString(),
                    type: 'success'
                });
                self.$emit('cc-updated');
            }).catch(() => {}).finally(() => {
                self.ccSaving = false;
            });
        },
        avatar(user) {
            if (!user) {
                return '';
            }
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
        formatDate(value) {
            if (!value) {
                return '';
            }
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format);
        },
        approverStatusClass(status) {
            if (status === 'approved') {
                return 'bg-green-100 text-green-800';
            }
            if (status === 'rejected') {
                return 'bg-red-100 text-red-800';
            }
            return 'bg-yellow-100 text-yellow-800';
        },
        approverStatusLabel(status) {
            if (status === 'approved') {
                return this.$t('Approved');
            }
            if (status === 'rejected') {
                return this.$t('Rejected');
            }
            return this.$t('Pending');
        },
    },
};
</script>

<style scoped>
.ticket-aside-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.025em;
}
</style>
